import { SITE_STATE, siteContentVersion } from '@/lib/siteContent';
import { useSyncExternalStore } from 'react';

/**
 * Placeholders for content that is still on its way from the CMS.
 *
 * The site holds no sample rows any more: a list is either what the API sent or
 * nothing at all. That leaves a gap between the page painting and the feed
 * landing, and an empty grid there reads as "there is nothing", which is a
 * different and wrong message. These fill the gap with the shape of what is
 * coming.
 */

/** True while the first feed is still in flight. Re-renders when it lands. */
export function useSiteLoading(): boolean {
    return useSyncExternalStore(
        siteContentVersion.subscribe,
        () => SITE_STATE.hydrating,
        () => true,
    );
}

/** One grey block. `animate-pulse` is dropped for anyone who asked for less motion. */
export function Skeleton({ className = '', style }: { className?: string; style?: React.CSSProperties }) {
    return (
        <div
            aria-hidden="true"
            style={style}
            className={`animate-pulse rounded-[4px] bg-[#ececec] motion-reduce:animate-none ${className}`}
        />
    );
}

/** The shape of an article or story card: picture, headline, byline row. */
export function CardSkeleton() {
    return (
        <article className="flex flex-col rounded-[8px] bg-[#f5f5f5] p-4">
            <Skeleton className="h-[170px] w-full" />
            <Skeleton className="mt-4 h-[13px] w-[88%]" />
            <Skeleton className="mt-2 h-[13px] w-[62%]" />
            <div className="mt-4 flex items-center gap-2">
                <Skeleton className="size-6 rounded-full" />
                <Skeleton className="h-[11px] w-[90px]" />
            </div>
        </article>
    );
}

/** A grid of card placeholders, matching the grid it stands in for. */
export function CardGridSkeleton({ count = 8 }: { count?: number }) {
    return (
        <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4" role="status" aria-label="Loading">
            {Array.from({ length: count }, (_, i) => (
                <CardSkeleton key={i} />
            ))}
        </div>
    );
}

/** The shape of a document card: cover, title, then the row of actions. */
export function DocCardSkeleton() {
    return (
        <article className="flex flex-col rounded-[8px] bg-[#f5f5f5] p-4">
            <Skeleton className="h-[170px] w-full rounded-[4px]" />
            <Skeleton className="mt-4 h-[13px] w-[90%]" />
            <Skeleton className="mt-2 h-[13px] w-[55%]" />
            <div className="mt-4 flex items-center gap-2">
                <Skeleton className="h-[26px] w-[26px] rounded-full" />
                <Skeleton className="h-[26px] w-[26px] rounded-full" />
                <Skeleton className="h-[26px] w-[26px] rounded-full" />
            </div>
        </article>
    );
}

/** A grid of document placeholders, matching the grid it stands in for. */
export function DocGridSkeleton({ count = 12 }: { count?: number }) {
    return (
        <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4" role="status" aria-label="Loading">
            {Array.from({ length: count }, (_, i) => (
                <DocCardSkeleton key={i} />
            ))}
        </div>
    );
}

/**
 * The Top-and-Recent band that sits above a listing grid.
 *
 * This one exists for a specific reason. The band renders nothing until the feed
 * names a top article, so without a placeholder it springs into being at full
 * height and shoves the grid, the pagination and the footer down the page. That
 * measured as a 0.47 layout shift on the blog, well past the 0.1 Google counts
 * as good. The shape here is the band's shape, so the arrival costs nothing.
 */
export function TopAndRecentSkeleton({ recent = 3 }: { recent?: number }) {
    return (
        <section className="bg-white pb-12 pt-12 lg:pb-16 lg:pt-16" role="status" aria-label="Loading">
            <div className="mx-auto flex max-w-[1640px] flex-col gap-10 px-5 lg:flex-row lg:gap-14 lg:px-[140px]">
                <div className="lg:w-[55%] lg:shrink-0">
                    <Skeleton className="h-[13px] w-[110px]" />
                    <Skeleton className="mt-6 h-[300px] w-full rounded-[14px] lg:h-[420px]" />
                </div>

                <div className="min-w-0 lg:flex-1">
                    <Skeleton className="h-[13px] w-[130px]" />
                    <div className="mt-6 flex flex-col gap-7">
                        {Array.from({ length: recent }, (_, i) => (
                            <div key={i} className="flex gap-4 lg:gap-5">
                                <Skeleton className="h-[86px] w-[110px] shrink-0 rounded-[6px] lg:h-[96px] lg:w-[128px]" />
                                <div className="min-w-0 flex-1">
                                    <Skeleton className="h-[13px] w-[94%]" />
                                    <Skeleton className="mt-2 h-[13px] w-[70%]" />
                                    <Skeleton className="mt-3 h-[11px] w-[120px]" />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}

/**
 * A whole page that is still arriving: a hero, then the shape of the bands under
 * it. Used by the programme, product and project pages, which render nothing at
 * all until the feed names the record.
 *
 * The reserved height is the point, as with the article body. These pages go from
 * a 708px placeholder to roughly 4,600px of content in one step, and everything
 * the shell puts below `main` — the feedback band, the footer — was on screen for
 * that step and got shoved down, scoring 0.16 on its own. More than a screen of
 * placeholder keeps them below the fold until the content is there.
 */
export function PageSkeleton() {
    return (
        <div className="min-h-[150vh]" role="status" aria-label="Loading">
            <Skeleton className="h-[320px] w-full rounded-none lg:h-[420px]" />
            <div className="mx-auto max-w-[1640px] px-5 py-16 lg:px-[140px]">
                <Skeleton className="h-[28px] w-[46%] lg:h-[38px]" />
                <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                    {Array.from({ length: 3 }, (_, i) => (
                        <div key={i}>
                            <Skeleton className="h-[150px] w-full" />
                            <Skeleton className="mt-4 h-[13px] w-[85%]" />
                            <Skeleton className="mt-2 h-[13px] w-[60%]" />
                        </div>
                    ))}
                </div>
                <Skeleton className="mt-16 h-[28px] w-[38%] lg:h-[38px]" />
                <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
                    {Array.from({ length: 4 }, (_, i) => (
                        <Skeleton key={i} className="h-[190px] w-full" />
                    ))}
                </div>
            </div>
        </div>
    );
}

/** The article page: headline, meta line, cover and a few paragraphs. */
export function ArticleSkeleton() {
    return (
        <div className="mx-auto min-h-[150vh] max-w-[1640px] px-5 lg:px-[140px]" role="status" aria-label="Loading">
            <Skeleton className="h-[34px] w-[70%] lg:h-[46px]" />
            <Skeleton className="mt-3 h-[34px] w-[45%] lg:h-[46px]" />
            <div className="mt-6 flex items-center gap-3">
                <Skeleton className="size-9 rounded-full" />
                <Skeleton className="h-[12px] w-[160px]" />
            </div>
            <Skeleton className="mt-8 h-[220px] w-full rounded-[14px] lg:h-[520px]" />
            {[92, 97, 84, 95, 70].map((width, i) => (
                <Skeleton key={i} className="mt-4 h-[12px]" style={{ width: `${width}%` }} />
            ))}
        </div>
    );
}
